import { Fragment } from "react"
import type { BaseSyntheticEvent, FC, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import { Button, type TButtonProps } from "@/3514/components/shared/button/button.component"
import { ESessionModality } from "@/3514/types"

import Message from "./session-info.component.message"
import classes from "./session-info.styles"
import type {
    TSessionInfoExtraPartyData as TExtraPartyParticipant,
    _TSessionInfoMessageComponentProps as TMessageProps,
    TSessionInfoParticipant as TParticipant,
    _TSessionInfoBodyProps as TProps
} from "./session-info.types"

const SessionInfoBody: FC<TProps> = ({
    viewerRole,
    colorSchema,
    sessionTimeState,
    sessionsQueryKey,
    data,
    onAttendeesListHover,
    renderAttendanceListItem,
    renderButtonsProps,
    renderMessagesProps,
    renderNotice,
    isAccordionExpanded,
    isAccordionExpandable
}: TProps): ReactElement => {
    const renderedButtonsProps: TButtonProps[] = renderButtonsProps({
        data,
        viewerRole,
        sessionTimeState,
        sessionsQueryKey,
        isAccordionExpandable,
        isAccordionExpanded
    })

    const renderedMessagesProps: TMessageProps[] = renderMessagesProps({
        session: data.session,
        sessionsQueryKey,
        isAccordionExpandable,
        isAccordionExpanded
    })

    const attendeesList: TParticipant[] = [
        ...data.session.participants,
        ...(isEmpty(data.extraParties)
            ? []
            : map(
                  data.extraParties,
                  (e: TExtraPartyParticipant): TParticipant => ({
                      id: e.id,
                      firstName: e.firstName,
                      lastName: null,
                      rsvpStatus: e.invitationStatus,
                      attendanceStatus: e.attendanceStatus,
                      extraPartyRole: e.label,
                      isExtraParty: true,
                      isGuest: false,
                      isHost: false
                  })
              )),
        {
            id: data.coach.id,
            firstName: data.coach.firstName,
            photo: data.coach.photo,
            lastName: String(""),
            rsvpStatus: null,
            attendanceStatus: null,
            extraPartyRole: null,
            isExtraParty: false,
            isGuest: false,
            isHost: true
        }
    ]

    async function handleContainerHover(e: BaseSyntheticEvent): Promise<void> {
        const elem: HTMLElement = (e.target as HTMLElement).closest("[data-index]")

        if (elem === null) return void 0

        const participantId: number = Number(elem.getAttribute("data-index"))
        const sessionModality: string = elem.getAttribute("data-modality")

        return await onAttendeesListHover(participantId, sessionModality as ESessionModality)
    }

    return (
        <div className={classes.body.container({ colorSchema, isAccordionExpanded, isAccordionExpandable })}>
            <div className={classes.body.messages.container()} hidden={isEmpty(renderedMessagesProps)}>
                {map<TMessageProps, ReactElement>(
                    renderedMessagesProps,
                    (props: TMessageProps, idx: number): ReactElement => (
                        <Message {...props} key={`si-msg-${data.session.id}-${idx}`} />
                    )
                )}
            </div>

            <div
                role="list"
                className={classes.body.list.container({ length: attendeesList.length })}
                {...(isAccordionExpanded && { onMouseOver: handleContainerHover, onFocus: handleContainerHover })}
            >
                {map<TParticipant, ReactElement>(
                    attendeesList,
                    (p: TParticipant): ReactElement => (
                        <Fragment key={`${data.session.id}-${p.id}`}>
                            {renderAttendanceListItem({
                                viewerRole,
                                participant: p,
                                groupId: data.session.groupId,
                                modality: data.session.modality
                            })(isAccordionExpanded)}
                        </Fragment>
                    )
                )}
            </div>

            <div className={classes.body.bottom()} hidden={isEmpty(renderedButtonsProps)}>
                {renderNotice({
                    isUpcoming: sessionTimeState.isUpcoming,
                    isLive: sessionTimeState.isLive,
                    modality: data.session.modality,
                    isMissed: data.session.isMissed,
                    minDaysToEdit: data.session.minHoursToEdit / 24
                })}

                <div className={classes.body.buttons()}>
                    {map<TButtonProps, ReactElement>(
                        renderedButtonsProps,
                        (props: TButtonProps, idx: number): ReactElement => (
                            <Button {...props} key={`si-btn-${data.session.id}-${idx}`} />
                        )
                    )}
                </div>
            </div>
        </div>
    )
}

SessionInfoBody.displayName = "SessionInfoBody"

export default SessionInfoBody
